import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { PasswordInput } from '../../shared/components/common/password-input/PasswordInput';
import { Button } from 'primereact/button';
import { Link, useNavigate } from 'react-router-dom';
import './SignupPage.scss';
import { getAuthConfig, resendVerification, signUp } from './services/SignupService';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../shared/contexts/auth/AuthContext';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import { LOGIN, PROJECTS_ADD } from '../../shared/routes/path';
import {
  REACT_APP_CLIENT_ID,
  REACT_APP_IS_DEBUG,
  REACT_APP_RECAPTCHA_SITE_KEY,
} from '../../shared/utils/config';
import { PRIVACY_POLICY, TERMS_OF_USE } from '../../shared/utils/urls';
import useDataLayer, { ISignUpDataLayer } from '../../shared/hooks/global/UseDataLayer';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import TestimonialSlider from '../../shared/components/testimonial-slider/TestimonialSlider';
import HoneypotField from '../../shared/components/common/honeypot-field/HoneypotField';

const SignupPage: React.FC = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const { pushDataLayer } = useDataLayer();

  const [apiError, setApiError] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [emailVerificationEnabled, setEmailVerificationEnabled] = useState(false);
  const [fingerprint, setFingerprint] = useState('');
  const cooldownRef = useRef<NodeJS.Timeout | null>(null);
  const formLoadedAt = useRef(Date.now());

  useEffect(() => {
    document.body.classList.add('visible', 'white');
    getAuthConfig()
      .then((res) => {
        setEmailVerificationEnabled(res.data.emailVerification);
        if (res.data.fingerprintProtection) {
          FingerprintJS.load()
            .then((fp) => fp.get())
            .then((result) => setFingerprint(result.visitorId));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
      return;
    }
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [resendCooldown > 0]);

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0 || !registeredEmail) return;
    setResendSuccess(false);
    try {
      const response = await resendVerification(registeredEmail);
      const retryAfter = response?.data?.retryAfter || 600;
      setResendSuccess(true);
      setResendCooldown(retryAfter);
      localStorage.setItem('resendCooldownUntil', String(Date.now() + retryAfter * 1000));
    } catch (err: any) {
      const retryAfter = err?.response?.data?.retryAfter;
      if (retryAfter) {
        setResendCooldown(retryAfter);
        localStorage.setItem('resendCooldownUntil', String(Date.now() + retryAfter * 1000));
      }
    }
  }, [resendCooldown, registeredEmail]);

  const onSubmit = async (formData: any) => {
    if (!recaptchaRef.current) {
      setApiError('Please turn on the reCAPTCHA');
      return;
    }

    const token = !REACT_APP_IS_DEBUG ? await recaptchaRef.current?.executeAsync() : '';
    const userData = {
      firstName: '',
      lastName: '',
      username: formData.email,
      password: formData.password,
      language: i18n.language?.split?.('-')?.[0] || i18n.language || 'en',
      client_id: REACT_APP_CLIENT_ID,
      grant_type: 'password',
      browserData: JSON.stringify(navigator.userAgent),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      recaptchaToken: token,
      fingerprint,
      contactReason: formData.contactReason ?? '',
      formFillTime: String(Date.now() - formLoadedAt.current),
    };

    const urlEncodedData = new URLSearchParams();
    for (const key in userData) {
      if (userData.hasOwnProperty(key)) {
        urlEncodedData.append(key, (userData as Record<string, string>)[key]);
      }
    }

    await signUp(urlEncodedData)
      .then((response) => {
        if (response.data.requiresVerification) {
          setApiError(null);
          setRegisteredEmail(formData.email);
          setVerificationSent(true);
          setResendCooldown(600);
          localStorage.setItem('resendCooldownUntil', String(Date.now() + 600 * 1000));
          return;
        }

        login(response.data);

        pushDataLayer<ISignUpDataLayer>({
          event: 'sign_up',
          email_hashed: formData.email,
          account_type: 'TRIAL',
          user_id: response.data.user.id.toString(),
        });

        navigate(PROJECTS_ADD);
      })
      .catch((err) => {
        if (err.statusCode === 401) {
          navigate(LOGIN);
          return;
        }

        setApiError(err.response.data.error);
      });
  };

  const clearApiError = () => {
    setApiError(null);
  };

  return (
    <div className="signup-page auth-pages">
      <div className="auth-pages__left">
        <div className="auth-pages__image">
          <div className="auth-pages__logo">
            <a href="https://chatbeat.com">
              <div className="logo-box" />
            </a>
          </div>
          <div className="signup-testimonials">
            <TestimonialSlider />
          </div>
        </div>
      </div>
      <div className="auth-pages__right">
        <div className="auth-pages__belt">
          <a href="https://chatbeat.com">
            <div className="logo-box logo-box--second" />
          </a>
        </div>
        {verificationSent ? (
          <div className="form-box form-box--verifty form-box--auth">
            <div className="form-box__title">{t('checkYourEmail')}</div>
            <div className="form-box__row form-box__row--login">{t('verificationEmailSent')}</div>
            {resendSuccess && (
              <div className="form-box__row form-box__row--success">
                {t('resendVerificationSuccess')}
              </div>
            )}
            <div className="form-box__row form-box__row--resend">
              <Button
                className="btn btn--link"
                label={
                  resendCooldown > 0
                    ? `${t('resendVerification')} (${Math.floor(resendCooldown / 60)}:${String(
                        resendCooldown % 60
                      ).padStart(2, '0')})`
                    : t('resendVerification')
                }
                type="button"
                disabled={resendCooldown > 0}
                onClick={handleResend}
              />
            </div>
            <Link to={LOGIN}>
              <Button className="btn" label={t('backToLogin')} />
            </Link>
          </div>
        ) : (
          <div className="form-box form-box--auth">
            <div className="form-box__title">{t('crateAnAccount')}:</div>
            <div className="form-box__row form-box__row--login">
              {t('alreadyHaveAccount')}? <Link to="/login">{t('signIn')}</Link>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="form-box__form">
              <HoneypotField registration={register('contactReason')} />
              <div className="form-box__row">
                <div className="form-box__label">{t('emailAddress')}:</div>
                <InputText
                  onInput={clearApiError}
                  placeholder={t('name@company.com')}
                  {...register('email', {
                    required: t('fieldIsRequired'),
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: t('wrongEmailAddress'),
                    },
                  })}
                  className={errors.email || apiError ? 'form-box__invalid' : ''}
                />
                {errors.email && <span>{errors.email.message as string}</span>}
              </div>

              <div className="form-box__row">
                <div className="form-box__label">{t('password')}:</div>
                <PasswordInput
                  onInput={clearApiError}
                  {...register('password', { required: t('fieldIsRequired') })}
                  className={errors.password || apiError ? 'form-box__invalid' : ''}
                />
                {errors.password && <span>{errors.password.message as string}</span>}
                {apiError && <span>{apiError}</span>}
              </div>

              <Button className="btn" label={t('signUp')} type="submit" />

              {!emailVerificationEnabled && (
                <div className="form-box__notification">{t('onceYouSignup')}</div>
              )}
            </form>
          </div>
        )}
        <div className="auth-pages__links links">
          <Link to={PRIVACY_POLICY}>{t('privacyPolicy')}</Link>
          <span className="links__dot" />
          <Link to={TERMS_OF_USE}>{t('termsAndConditions')}</Link>
        </div>
      </div>
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={REACT_APP_RECAPTCHA_SITE_KEY || 'sample'}
        size="invisible"
      />
    </div>
  );
};

export default SignupPage;
